import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import CardResults from '../../components/CardResults/CardResults';
import { askCards } from '../../services/readingService';
import FeaturePromo from '../../components/FeaturePromo/FeaturePromo';
import { ShareBox } from '../../components/ReadingFooter/ReadingFooter';
import './AskAnAi.css';

const TYPEWRITER_QUESTIONS = [
  'Will I find love this year?',
  'Should I take the new job?',
  'What is he thinking about me?',
  'Is this the right decision?',
  'What does my future look like?',
];

const AskAnAi = () => {
  const [question, setQuestion] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [readingData, setReadingData] = useState(null);
  const [streamingInterpretation, setStreamingInterpretation] = useState('');
  const [typedPlaceholder, setTypedPlaceholder] = useState('');
  const [userHasTyped, setUserHasTyped] = useState(false);
  const interpretationRef = useRef('');

  useEffect(() => {
    if (userHasTyped) return;
    let cancelled = false;
    let timeoutId;

    const sleep = (ms) =>
      new Promise((resolve) => {
        timeoutId = setTimeout(resolve, ms);
      });

    const run = async () => {
      let questionIndex = 0;
      while (!cancelled) {
        const sample = TYPEWRITER_QUESTIONS[questionIndex % TYPEWRITER_QUESTIONS.length];
        for (let i = 0; i <= sample.length; i++) {
          if (cancelled) return;
          setTypedPlaceholder(sample.slice(0, i));
          await sleep(70);
        }
        await sleep(1800);
        for (let i = sample.length; i >= 0; i--) {
          if (cancelled) return;
          setTypedPlaceholder(sample.slice(0, i));
          await sleep(40);
        }
        await sleep(350);
        questionIndex += 1;
      }
    };

    run();
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [userHasTyped]);

  const handleAsk = useCallback(async (e) => {
    e.preventDefault();
    const q = question.trim();
    if (!q || isLoading) return;
    setError('');
    setIsLoading(true);
    setReadingData(null);
    interpretationRef.current = '';
    setStreamingInterpretation('');
    await askCards(q, {
      onCards: (data) => {
        setReadingData(data);
      },
      onToken: (token) => {
        interpretationRef.current += token;
        setStreamingInterpretation(interpretationRef.current);
      },
      onDone: () => {
        setIsLoading(false);
      },
      onError: (msg) => {
        setIsLoading(false);
        setError(msg || 'Something went wrong. Please try again.');
      },
    });
  }, [question, isLoading]);

  const handleReset = useCallback(() => {
    setQuestion('');
    setReadingData(null);
    setStreamingInterpretation('');
    interpretationRef.current = '';
  }, []);

  const pageTitle = 'Ask an AI Tarot Reader — Tarot LLM Reading | NowTarot AI';
  const pageDescription = 'Ask any question and get an instant tarot LLM reading. Four tarot cards are drawn and interpreted in context by an AI tarot reader. Free, no sign-up required.';
  const socialDescription = 'Ask any question and get an instant tarot LLM reading with four cards drawn and interpreted for your question. Free, no sign-up required.';
  const isStreamSource = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('source') === 'stream';
  }, []);
  const askAnAiJsonLd = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Ask an AI Tarot Reader - Tarot LLM Reading",
    "description": pageDescription,
    "url": "https://nowtarot.com/ask-an-ai/"
  };

  return (
    <div className={`aai-page ${isStreamSource ? 'aai-page-stream' : ''}`}>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content="ai tarot reading, free tarot reading, ask tarot, online tarot cards, ai tarot reader, tarot question, tarot llm reader, llm tarot, ai tarot card reader" />
        <link rel="canonical" href="https://nowtarot.com/ask-an-ai/" />
        <meta property="og:title" content="Ask an AI Tarot Reader — Tarot LLM Reading" />
        <meta property="og:description" content={socialDescription} />
        <meta property="og:url" content="https://nowtarot.com/ask-an-ai/" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Ask an AI Tarot Reader — Tarot LLM Reading" />
        <meta name="twitter:description" content={socialDescription} />
        <script type="application/ld+json">{JSON.stringify(askAnAiJsonLd)}</script>
      </Helmet>

      {!isStreamSource && <Header />}

      <div className={`aai-hero ${isStreamSource ? 'aai-hero-stream' : ''}`}>
        <h1 className="aai-title">
          {isStreamSource ? 'Ask the Cards Your Question Using YouTube Chat' : 'Ask the Cards a Question'}
        </h1>
        <p className="aai-subtitle">
          {isStreamSource
            ? 'Live questions go in, four tarot cards come out, and the AI reads them in real time.'
            : `Type your question below. We'll draw four tarot cards and show you
          what they mean — instantly, for free.`}
        </p>
        <form className={`aai-form ${isStreamSource ? 'aai-form-stream' : ''}`} onSubmit={handleAsk}>
          {isStreamSource && <span className="aai-stream-question-label">Live Question</span>}
          <input
            type="text"
            className={`aai-input ${isStreamSource ? 'aai-input-stream' : ''}`}
            placeholder={typedPlaceholder}
            value={question}
            onChange={(e) => {
              setQuestion(e.target.value);
              if (!userHasTyped && e.target.value) setUserHasTyped(true);
              if (userHasTyped && !e.target.value) setUserHasTyped(false);
            }}
            disabled={isLoading}
            maxLength={500}
          />
          <button
            type="submit"
            className={`aai-btn ${isStreamSource ? 'aai-btn-stream' : ''}`}
            disabled={!question.trim() || isLoading}
          >
            {isLoading ? 'Drawing Cards...' : 'Ask the Cards'}
          </button>
        </form>
        {error && <p className="hero-ask-error" role="alert">{error}</p>}

      </div>

      {readingData && (
        <div className="aai-results">
          {isStreamSource && (
            <div className="aai-stream-promo">
              <p className="aai-stream-promo-title">Want your own private free AI tarot reading?</p>
              <a href="https://nowtarot.com" className="aai-stream-promo-link">
                Visit NowTarot.com
              </a>
            </div>
          )}

          <CardResults
            cards={readingData.cards}
            verdict={readingData.verdict}
            interpretation={streamingInterpretation}
            isStreaming={isLoading}
            isStreamMode={isStreamSource}
            hideShare={!isStreamSource}
          />

          {!isStreamSource && (
            <FeaturePromo variant="modal" />
          )}
          {!isStreamSource && streamingInterpretation && !isLoading && <ShareBox />}

          <button className="aai-again-btn" onClick={handleReset}>
            Ask Another Question
          </button>
        </div>
      )}

      {!readingData && (
        <div className="aai-info">
          <h2>How It Works</h2>
          <div className="aai-steps">
            <div className="aai-step">
              <div className="aai-step-num">1</div>
              <h3>Ask Your Question</h3>
              <p>Type any question — about love, career, money, or life.</p>
            </div>
            <div className="aai-step">
              <div className="aai-step-num">2</div>
              <h3>Cards Are Drawn</h3>
              <p>Four tarot cards are drawn based on the energy of your question.</p>
            </div>
            <div className="aai-step">
              <div className="aai-step-num">3</div>
              <h3>Read Your Cards</h3>
              <p>See each card's meaning, orientation, and overall verdict.</p>
            </div>
          </div>

          <div className="aai-seo">
            <h2>Powered by an AI Tarot LLM Reader</h2>
            <p>
              NowTarot uses a large language model fine-tuned for tarot interpretation — a tarot LLM reader
              that understands the symbolism, suits, and archetypes behind every card. Unlike generic AI
              chatbots, our tarot LLM reader draws from the full 78-card Rider-Waite tradition, weighing
              upright and reversed meanings, card positions, and how they interact with your specific question.
            </p>
            <p>
              Each reading is unique. The AI tarot reader considers the energy of your question, selects
              cards with weighted randomness, and then interprets them together — not one by one, but as
              a spread. The result is a nuanced, human-feeling reading delivered in seconds, completely free.
            </p>
            <h3>Why Use an LLM-Powered Tarot Reader?</h3>
            <ul>
              <li><strong>Instant readings</strong> — no waiting, no scheduling, no sign-up</li>
              <li><strong>Context-aware interpretation</strong> — the AI reads cards in relation to your actual question</li>
              <li><strong>Full spread analysis</strong> — four cards read together, not in isolation</li>
              <li><strong>Available 24/7</strong> — ask as many questions as you want, any time</li>
            </ul>
          </div>
        </div>
      )}

      {isStreamSource && (
        <div className="aai-stream-info-wrap">
          <div className="aai-upsell aai-upsell-stream-persistent">
            <h3>How This Works</h3>
            <ol className="aai-upsell-list">
              <li>
                Your question helps the AI tarot reader turn the energy of
                what you asked into a four-card spread and interpretation.
              </li>
              <li>
                Want a private free AI reading of your own? Go to
                {' '}
                <a href="https://nowtarot.com" className="aai-upsell-link">
                  nowtarot.com
                </a>
                .
              </li>
            </ol>
            <a href="https://nowtarot.com" className="aai-upsell-cta">
              Start a Private Free Reading
            </a>
          </div>
        </div>
      )}

      {!isStreamSource && <Footer />}
    </div>
  );
};

export default AskAnAi;
